import { TripOutput } from '@allocab/allocab-passenger-shared-types';
import dayjs from 'dayjs';
import { useState } from 'react';

import { useAskUpdate } from '../../../../hooks/useAskUpdate';
import { useConfirmUpdate } from '../../../../hooks/useConfirmUpdate';
import { ToastManager } from '../../../../services/toast/utils';
import { t } from '../../../../services/translation/translation';
import { formatDate, formatDateForParisTZ, localDateToTzDate, parisLocalDate } from '../../../../utils/date';
import { formatPrice } from '../../../../utils/trip';
import { DiffView } from '../../../atomic/DiffView';
import { TimePicker } from '../../../atomic/TimePicker';
import { GradientButton } from '../../../button/GradientButton';
import { SecondaryButton } from '../../../button/SecondaryButton';
import { DatePicker } from '../../../date-picker/DatePicker';
import { ModalTitle } from '../../../Modal';
import { Spinner } from '../../../Spinner';

type Props = {
  initialDate: number;
  trip: TripOutput;
  onClose: () => void;
};

export const UpdateTripDepartureAt = ({ initialDate, trip, onClose }: Props) => {
  const [departureAt, setDepartureAt] = useState<number>(trip.resume.departureAt);

  const { data: askUpdateData, mutate: anonymousAskUpdate, isLoading: isAsking, reset } = useAskUpdate();

  const { mutate: confirmUpdate, isLoading: isConfirming } = useConfirmUpdate(trip.key);

  const handleSelectDate = (timestamp: number | undefined) => {
    if (timestamp && timestamp === trip.resume.departureAt) {
      ToastManager.show({ severity: 'warning', message: t('update-trip.modal.warning.same-date') });
    } else if (timestamp && timestamp < Date.now()) {
      ToastManager.show({ severity: 'warning', message: t('update-trip.modal.warning.date-in-past') });
    } else {
      anonymousAskUpdate({ newDepartureAt: timestamp, key: trip.key });
    }
  };

  const handleConfirmDate = () => {
    confirmUpdate(
      { key: trip.key, id: askUpdateData?.id },
      {
        onSuccess: () => {
          ToastManager.show({ severity: 'success', message: t('update-trip.modal.success') });
          onClose();
        },
      },
    );
  };

  return (
    <div>
      <ModalTitle title={t('update-trip.modal.departure-at-button')} />

      {!askUpdateData ? (
        <div className="flex justify-center mt-5 items-center">
          {isAsking ? (
            <Spinner className="ml-5" />
          ) : (
            <div>
              <DatePicker
                hideActions={true}
                initialDate={initialDate}
                onChange={value => {
                  setDepartureAt(
                    localDateToTzDate(
                      `${formatDate(value ?? trip.resume.departureAt, 'YYYY-MM-DD')} ${formatDateForParisTZ(
                        departureAt,
                        'HH:mm:ss',
                        false,
                      )}`,
                      'Europe/Paris',
                    ),
                  );
                }}
              />
              <TimePicker
                hour={parisLocalDate(departureAt).hour()}
                minutes={parisLocalDate(departureAt).minute()}
                onTimeChange={(hour, minutes) => {
                  const date = parisLocalDate(departureAt).hour(hour).minute(minutes).valueOf();
                  setDepartureAt(date);
                }}
              />
              <div className="flex mt-5 justify-center gap-5">
                <SecondaryButton onClick={() => onClose()} className="w-full space-y-3 mt-2">
                  {t('common.cancel')}
                </SecondaryButton>
                <GradientButton
                  isLoading={isConfirming}
                  onClick={() => handleSelectDate(dayjs(departureAt).valueOf())}
                  className="w-full space-y-3 mt-2"
                >
                  {t('common.confirm')}
                </GradientButton>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <DiffView
            oldValue={formatDateForParisTZ(trip.resume.departureAt, t('formats.datetime'))}
            newValue={
              askUpdateData.newDepartureAt
                ? formatDateForParisTZ(askUpdateData.newDepartureAt, t('formats.datetime'))
                : undefined
            }
          />
          {askUpdateData.requirePriceUpdate === true && askUpdateData.newPrice ? (
            <div className="text-center mt-5 font-bold">
              {t('trip-screen.task-desc-edit-price', {
                newPrice: formatPrice(askUpdateData.newPrice.amountWithTax, askUpdateData.newPrice.currency),
                oldPrice: formatPrice(trip.resume.price.amountWithTax, trip.resume.price.currency),
              })}
            </div>
          ) : null}
          <div className="text-center mt-3">{t('trip-screen.asking-agreement-details')}</div>
          <div className="flex justify-center gap-5">
            <SecondaryButton onClick={() => reset()} className="w-full space-y-3 mt-2">
              {t('common.cancel')}
            </SecondaryButton>
            <GradientButton
              isLoading={isConfirming}
              onClick={() => handleConfirmDate()}
              className="w-full space-y-3 mt-2"
            >
              {t('common.confirm')}
            </GradientButton>
          </div>
        </div>
      )}
    </div>
  );
};
